import React from "react";
import { Truck, MapPin, Globe, Package, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";

export const ShippingPolicy: React.FC = () => {
  return (
    <div className='max-w-4xl mx-auto px-6 py-12'>
      {/* Page Header */}
      <div className='mb-10 text-center'>
        <div className='inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-4'>
          <Truck className='h-8 w-8 text-blue-600' />
        </div>
        <h1 className='text-3xl font-bold text-gray-900 mb-2'>Shipping Policy</h1>
        <p className='text-lg text-gray-600 max-w-2xl mx-auto'>
          Everything you need to know about our shipping process, delivery timeframes, and policies.
        </p>
      </div>

      {/* Introduction */}
      <Card className='p-6 border-0 shadow-sm mb-8'>
        <p className='text-gray-700'>
          Thank you for shopping with Stedshop LTD. This Shipping Policy explains how we deliver
          your orders, our delivery timelines, and our approach to domestic and international
          shipping.
        </p>
      </Card>

      {/* 1. Domestic Shipping */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='domestic'>
        <div className='flex items-start mb-4'>
          <MapPin className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>
              1. Domestic Shipping (Within Nigeria)
            </h2>
          </div>
        </div>

        <ul className='space-y-3 text-gray-700'>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              All orders placed on https://stedshop.com.ng are processed and dispatched within{" "}
              <strong>1–2 business days</strong> (excluding weekends and public holidays).
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              <strong>Delivery Time:</strong> Customers should expect to receive their order within{" "}
              <strong>1–3 business days</strong> after dispatch, depending on location.
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              We partner with trusted third-party logistics companies to ensure safe and timely
              delivery.
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              Once your order is shipped, you'll receive a tracking number via SMS or email to
              monitor your delivery in real time.
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              <strong>Delivery Address:</strong> Ensure your shipping address is complete and
              accurate. We are not responsible for delays or losses due to incorrect or incomplete
              addresses.
            </span>
          </li>
        </ul>

        <div className='mt-6 p-4 bg-blue-50 rounded-lg'>
          <p className='text-gray-700'>
            <strong>Note:</strong> Our delivery partners do not enter private residences. Delivery
            is considered complete once the package is handed over at your gate, front desk, or
            designated drop-off point.
          </p>
        </div>
      </Card>

      {/* 2. International Shipping */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='international'>
        <div className='flex items-start mb-4'>
          <Globe className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>2. International Shipping</h2>
          </div>
        </div>

        <ul className='space-y-3 text-gray-700 mb-6'>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>International shipping is available upon customer request.</span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              If you are ordering from outside Nigeria, please contact our customer support team at{" "}
              <strong>support@stedshop.com.ng</strong> before or immediately after placing your
              order.
            </span>
          </li>
        </ul>

        <p className='text-gray-700 mb-3'>We will work with you to determine:</p>
        <ol className='list-decimal pl-6 space-y-2 text-gray-700 mb-6'>
          <li>The most reliable and cost-effective shipping method</li>
          <li>Estimated delivery time</li>
          <li>
            Applicable shipping fees and import/customs duties (which are the customer's
            responsibility)
          </li>
        </ol>

        <p className='text-gray-700 mb-4'>
          All international orders are shipped via international courier partners (e.g., DHL, FedEx)
          at the customer's expense.
        </p>

        <div className='mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200'>
          <div className='flex items-start'>
            <AlertCircle className='h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0' />
            <div>
              <p className='text-gray-700 font-semibold mb-2'>
                Stedshop LTD is not responsible for:
              </p>
              <ol className='list-decimal pl-5 space-y-1 text-gray-700'>
                <li>Customs delays, import taxes, or duties</li>
                <li>Lost or damaged packages due to customs inspection or local postal handling</li>
                <li>Non-compliance with import regulations in the destination country</li>
              </ol>
            </div>
          </div>
        </div>
      </Card>

      {/* 3. Shipping Costs */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-green-50' id='costs'>
        <div className='flex items-start mb-4'>
          <div>
            <h2 className='text-xl font-bold text-gray-900'>3. Shipping Costs</h2>
          </div>
        </div>

        <ul className='space-y-3 text-gray-700'>
          <li className='flex items-start'>
            <span className='text-green-600 mr-2'>✓</span>
            <span>
              <strong>Domestic shipping is FREE</strong>
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              International shipping fees are quoted on a case-by-case basis after consultation with
              the customer.
            </span>
          </li>
        </ul>
      </Card>

      {/* 4. Order Tracking & Delivery Issues */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='tracking'>
        <div className='flex items-start mb-4'>
          <Package className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>4. Order Tracking & Delivery Issues</h2>
          </div>
        </div>

        <ul className='space-y-3 text-gray-700'>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>Use your tracking number (sent via email) to monitor your shipment.</span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              If your order is delayed beyond <strong>5 business days (domestic)</strong> or the
              agreed international timeframe, contact us at <strong>support@stedshop.com.ng</strong>
              .
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              For lost or damaged items, report within <strong>48 hours of delivery</strong>. Photos
              may be requested for verification.
            </span>
          </li>
        </ul>
      </Card>

      {/* 5. Risk of Loss */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='risk'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>5. Risk of Loss</h2>
        <ul className='space-y-3 text-gray-700'>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              Title and risk of loss for all items remain with Stedshop LTD until the item is
              delivered to your specified address.
            </span>
          </li>
          <li className='flex items-start'>
            <span className='text-blue-600 mr-2'>•</span>
            <span>
              Once delivered (or left at your premises per courier protocol), responsibility
              transfers to you.
            </span>
          </li>
        </ul>
      </Card>

      {/* Contact Section */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-gray-50'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>Questions?</h2>
        <p className='text-gray-700 mb-2'>
          Contact our support team: <strong>support@stedshop.com.ng</strong>
        </p>
        <p className='text-gray-700 mt-4 font-semibold text-center'>
          Thank you for choosing Stedshop LTD — your trusted source for authentic fashion, delivered
          fast across Nigeria and beyond!
        </p>
      </Card>
    </div>
  );
};
